(() => {
  const initPioneerBuilder = root => {
    if (root.dataset.pioneerReady === 'true') return;
    root.dataset.pioneerReady = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    root.pioneerController = controller;

    const tocLinks = Array.from(root.querySelectorAll('[data-toc-link]'));
    const sections = Array.from(root.querySelectorAll('[data-pioneer-section]'));
    const toc = root.querySelector('[data-pioneer-toc]');
    const tocSlot = root.querySelector('[data-pioneer-toc-slot]');
    const sidebar = root.querySelector('.pioneer-page-layout__sidebar');

    const getHeaderOffset = () => {
      const selectors = [
        '#site-header',
        '#shopify-section-header',
        '.shopify-section-group-header-group',
        'header',
        '.header-wrapper',
        '.section-header'
      ];
      let maxHeight = 0;

      selectors.forEach(selector => {
        const element = document.querySelector(selector);
        if (!element) return;
        maxHeight = Math.max(maxHeight, element.getBoundingClientRect().height);
      });

      return maxHeight + 24;
    };

    if (tocLinks.length && sections.length && toc && tocSlot && sidebar) {
      let manualActiveId = null;
      let manualLockUntil = 0;
      let ticking = false;
      let followState = 'normal';
      let fixedLeft = null;
      let fixedWidth = null;
      let slotHeight = null;

      const isDesktop = () => window.innerWidth > 920;

      const setActive = id => {
        tocLinks.forEach(link => {
          link.classList.toggle('is-active', link.dataset.targetId === id);
        });
      };

      const getCurrentSectionId = () => {
        const probe = getHeaderOffset() + 40;
        let currentId = null;

        sections.forEach(section => {
          if (section.getBoundingClientRect().top <= probe) {
            currentId = section.dataset.pioneerSection;
          }
        });

        return currentId || sections[0]?.dataset.pioneerSection || null;
      };

      const setSlotHeight = height => {
        const nextHeight = Math.round(height);
        if (slotHeight === nextHeight) return;
        tocSlot.style.height = `${nextHeight}px`;
        slotHeight = nextHeight;
      };

      const resetSlotHeight = () => {
        tocSlot.style.height = '';
        slotHeight = null;
      };

      const applyNormalState = () => {
        if (followState === 'normal') return;
        toc.classList.remove('pioneer-toc--fixed', 'pioneer-toc--bottom');
        toc.style.width = '';
        toc.style.left = '';
        resetSlotHeight();
        followState = 'normal';
        fixedLeft = null;
        fixedWidth = null;
      };

      const applyFixedState = (left, width, height) => {
        if (followState !== 'fixed') {
          toc.classList.remove('pioneer-toc--bottom');
          toc.classList.add('pioneer-toc--fixed');
          followState = 'fixed';
        }

        setSlotHeight(height);

        const roundedLeft = Math.round(left);
        const roundedWidth = Math.round(width);

        if (fixedLeft !== roundedLeft) {
          toc.style.left = `${roundedLeft}px`;
          fixedLeft = roundedLeft;
        }

        if (fixedWidth !== roundedWidth) {
          toc.style.width = `${roundedWidth}px`;
          fixedWidth = roundedWidth;
        }
      };

      const applyBottomState = height => {
        if (followState !== 'bottom') {
          toc.classList.remove('pioneer-toc--fixed');
          toc.classList.add('pioneer-toc--bottom');
          toc.style.left = '';
          toc.style.width = '100%';
          followState = 'bottom';
        }

        setSlotHeight(height);
        fixedLeft = null;
        fixedWidth = null;
      };

      const updateTocFollow = () => {
        if (!toc.classList.contains('pioneer-toc--sticky') || !isDesktop()) {
          applyNormalState();
          return;
        }

        const viewportTop = getHeaderOffset();
        const rootRect = root.getBoundingClientRect();
        const slotRect = tocSlot.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();
        const tocHeight = toc.offsetHeight;
        const rootTop = window.pageYOffset + rootRect.top;
        const rootBottom = rootTop + root.offsetHeight;
        const slotTop = window.pageYOffset + slotRect.top;
        const fixedStart = slotTop - viewportTop;
        const fixedEnd = rootBottom - tocHeight - viewportTop;

        if (window.pageYOffset < fixedStart) {
          applyNormalState();
        } else if (window.pageYOffset < fixedEnd) {
          applyFixedState(sidebarRect.left, sidebarRect.width, tocHeight);
        } else {
          applyBottomState(tocHeight);
        }
      };

      const updateActiveOnScroll = () => {
        if (Date.now() < manualLockUntil && manualActiveId) {
          setActive(manualActiveId);
          return;
        }

        manualActiveId = null;
        setActive(getCurrentSectionId());
      };

      const render = () => {
        updateTocFollow();
        updateActiveOnScroll();
        ticking = false;
      };

      const requestRender = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(render);
      };

      tocLinks.forEach(link => {
        link.addEventListener('click', event => {
          const targetId = link.dataset.targetId;
          const target = targetId ? document.getElementById(targetId) : null;
          if (!target) return;

          event.preventDefault();
          manualActiveId = targetId;
          manualLockUntil = Date.now() + 1400;
          setActive(targetId);

          const targetTop = window.pageYOffset + target.getBoundingClientRect().top - getHeaderOffset();
          window.scrollTo({ top: targetTop, behavior: 'smooth' });
        }, { signal });
      });

      window.addEventListener('scroll', requestRender, { passive: true, signal });
      window.addEventListener('resize', requestRender, { signal });
      window.addEventListener('load', requestRender, { signal });
      document.addEventListener('shopify:section:load', requestRender, { signal });
      document.addEventListener('shopify:section:reorder', requestRender, { signal });
      document.addEventListener('shopify:block:select', requestRender, { signal });
      document.addEventListener('shopify:block:deselect', requestRender, { signal });

      requestRender();
      setTimeout(requestRender, 150);
      setTimeout(requestRender, 500);
    }

    root.querySelectorAll('[data-pioneer-subscribe-form]').forEach(form => {
      const emailInput = form.querySelector('[data-pioneer-subscribe-email]');
      const submitButton = form.querySelector('[data-pioneer-subscribe-button]');
      const messageWrap = form.querySelector('[data-pioneer-subscribe-message]');
      const frameName = form.getAttribute('target');
      const iframe = frameName ? root.querySelector(`iframe[name="${frameName}"]`) : null;

      if (!emailInput || !submitButton || !messageWrap || !iframe) return;

      const successMessage = messageWrap.dataset.successMessage || 'Thanks for joining.';
      const processingMessage = 'Submitting...';
      const errorMessage = 'Something went wrong. Please try again.';
      const invalidMessage = 'Please enter a valid email address.';
      let submitting = false;
      let timeout = null;
      let iframeLoaded = false;

      const showMessage = (type, text) => {
        messageWrap.innerHTML = `<p class="pioneer-subscribe__message pioneer-subscribe__message--${type}">${text}</p>`;
      };

      const clearSubmitState = () => {
        submitting = false;
        submitButton.disabled = false;
        if (!timeout) return;
        clearTimeout(timeout);
        timeout = null;
      };

      iframe.addEventListener('load', () => {
        if (!submitting) return;
        iframeLoaded = true;
        clearSubmitState();
        form.reset();
        showMessage('success', successMessage);
      }, { signal });

      form.addEventListener('submit', event => {
        if (!emailInput.checkValidity()) {
          event.preventDefault();
          showMessage('error', invalidMessage);
          emailInput.reportValidity();
          return;
        }

        window.CueStorefront?.subscribeToKlaviyo?.(form, emailInput.value.trim());
        submitting = true;
        iframeLoaded = false;
        submitButton.disabled = true;
        showMessage('success', processingMessage);

        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          if (iframeLoaded || !submitting) return;
          clearSubmitState();
          showMessage('error', errorMessage);
        }, 12000);
      }, { signal });
    });
  };

  const destroyPioneerBuilder = root => {
    root.pioneerController?.abort();
    delete root.pioneerController;
    delete root.dataset.pioneerReady;
  };

  const initAll = scope => {
    const roots = [];
    if (scope.matches?.('[data-pioneer-builder]')) roots.push(scope);
    scope.querySelectorAll?.('[data-pioneer-builder]').forEach(root => roots.push(root));
    roots.forEach(initPioneerBuilder);
  };

  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = [];
    if (event.target.matches?.('[data-pioneer-builder]')) roots.push(event.target);
    event.target.querySelectorAll?.('[data-pioneer-builder]').forEach(root => roots.push(root));
    roots.forEach(destroyPioneerBuilder);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();
